import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const NotificationSettings = ({ className = "" }) => {
  const [settings, setSettings] = useState({
    emailAlerts: true,
    inAppBanner: true,
    pushNotifications: false,
    audioAlerts: true,
    criticalThreshold: 'immediate',
    highThreshold: '5min',
    mediumThreshold: '15min',
    lowThreshold: 'disabled',
    emailFrequency: 'realtime',
    quietHours: false,
    quietStart: '22:00',
    quietEnd: '06:00'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const thresholdOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: '1min', label: '1 minute' },
    { value: '5min', label: '5 minutes' },
    { value: '15min', label: '15 minutes' },
    { value: '30min', label: '30 minutes' },
    { value: '1hour', label: '1 hour' },
    { value: 'disabled', label: 'Disabled' }
  ];

  const emailFrequencyOptions = [
    { value: 'realtime', label: 'Real-time' },
    { value: 'hourly', label: 'Hourly digest' },
    { value: 'daily', label: 'Daily summary' },
    { value: 'weekly', label: 'Weekly report' }
  ];

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = () => {
    // Simulate saving settings
    console.log('Saving notification settings:', settings);
    // Show success message or toast
  };

  const handleResetSettings = () => {
    setSettings({
      emailAlerts: true,
      inAppBanner: true,
      pushNotifications: false,
      audioAlerts: true,
      criticalThreshold: 'immediate',
      highThreshold: '5min',
      mediumThreshold: '15min',
      lowThreshold: 'disabled',
      emailFrequency: 'realtime',
      quietHours: false,
      quietStart: '22:00',
      quietEnd: '06:00'
    });
  };

  return (
    <div className={`bg-card border border-border rounded-lg security-shadow-card ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
            <Icon name="Settings" size={16} className="text-accent" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Notification Settings</h2>
            <p className="text-sm text-muted-foreground">Configure alert preferences and thresholds</p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
          className="text-muted-foreground"
        />
      </div>
      {/* Settings Content */}
      <div className={`overflow-hidden security-transition ${isExpanded ? 'max-h-none' : 'max-h-0'}`}>
        <div className="p-4 space-y-6">
          {/* Notification Channels */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Notification Channels</h3>
            <div className="space-y-3">
              <Checkbox
                label="Email Alerts"
                description="Receive security alerts via email"
                checked={settings?.emailAlerts}
                onChange={(e) => handleSettingChange('emailAlerts', e?.target?.checked)}
              />
              <Checkbox
                label="In-App Banner Notifications"
                description="Show banner notifications in the application"
                checked={settings?.inAppBanner}
                onChange={(e) => handleSettingChange('inAppBanner', e?.target?.checked)}
              />
              <Checkbox
                label="Push Notifications"
                description="Browser push notifications (requires permission)"
                checked={settings?.pushNotifications}
                onChange={(e) => handleSettingChange('pushNotifications', e?.target?.checked)}
              />
              <Checkbox
                label="Audio Alerts"
                description="Play sound for critical security events"
                checked={settings?.audioAlerts}
                onChange={(e) => handleSettingChange('audioAlerts', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Alert Thresholds */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Alert Thresholds</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Critical Alerts"
                description="Immediate security threats"
                options={thresholdOptions}
                value={settings?.criticalThreshold}
                onChange={(value) => handleSettingChange('criticalThreshold', value)}
              />
              <Select
                label="High Priority Alerts"
                description="Important security events"
                options={thresholdOptions}
                value={settings?.highThreshold}
                onChange={(value) => handleSettingChange('highThreshold', value)}
              />
              <Select
                label="Medium Priority Alerts"
                description="Standard security notifications"
                options={thresholdOptions}
                value={settings?.mediumThreshold}
                onChange={(value) => handleSettingChange('mediumThreshold', value)}
              />
              <Select
                label="Low Priority Alerts"
                description="Informational notifications"
                options={thresholdOptions}
                value={settings?.lowThreshold}
                onChange={(value) => handleSettingChange('lowThreshold', value)}
              />
            </div>
          </div>

          {/* Email Settings */}
          {settings?.emailAlerts && (
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3">Email Settings</h3>
              <Select
                label="Email Frequency"
                description="How often to send email notifications"
                options={emailFrequencyOptions}
                value={settings?.emailFrequency}
                onChange={(value) => handleSettingChange('emailFrequency', value)}
                className="max-w-md"
              />
            </div>
          )}

          {/* Quiet Hours */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3">Quiet Hours</h3>
            <div className="space-y-3">
              <Checkbox
                label="Enable Quiet Hours"
                description="Suppress non-critical notifications during specified hours"
                checked={settings?.quietHours}
                onChange={(e) => handleSettingChange('quietHours', e?.target?.checked)}
              />
              
              {settings?.quietHours && (
                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Start Time</label>
                    <input
                      type="time"
                      value={settings?.quietStart}
                      onChange={(e) => handleSettingChange('quietStart', e?.target?.value)}
                      className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">End Time</label>
                    <input
                      type="time"
                      value={settings?.quietEnd}
                      onChange={(e) => handleSettingChange('quietEnd', e?.target?.value)}
                      className="w-full px-3 py-2 bg-input border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={handleResetSettings}
              iconName="RotateCcw"
            >
              Reset to Default
            </Button>
            <Button
              variant="default"
              onClick={handleSaveSettings}
              iconName="Save"
            >
              Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;